import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import ResponsivePageHeader from '../components/Common/ResponsivePageHeader';
import { performanceAPI } from '../services/api';
import { logger } from '../utils/logger';

interface SystemHealth {
  uptime_seconds: number;
  uptime_formatted: string;
  total_requests: number;
  total_errors: number;
  error_rate: number;
  avg_response_time: number;
  active_connections: number;
  endpoints_tracked: number;
  memory_usage: {
    rss_mb: number;
    vms_mb: number;
  };
  status: 'healthy' | 'degraded' | 'error';
  database: {
    response_time_ms: number;
    pool_size: number | string;
    connections_checked_out: number | string;
    connections_checked_in: number | string;
    status: 'healthy' | 'slow' | 'error';
  };
}

interface EndpointStats {
  endpoint: string;
  method: string;
  total_requests: number;
  avg_response_time: number;
  min_response_time: number;
  max_response_time: number;
  error_rate: number;
  requests_per_minute: number;
  p95_response_time: number;
  p99_response_time: number;
}

interface LiveMetrics {
  timestamp: string;
  recent_activity: Array<{
    endpoint: string;
    method: string;
    recent_requests: number;
    avg_response_time: number;
    last_request: string;
  }>;
  database_response_time: number;
  active_connections: number;
}

const PerformanceMonitoringDashboard: React.FC = () => {
  const { user } = useAuth();
  const { showError } = useNotification();

  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [endpointStats, setEndpointStats] = useState<EndpointStats[]>([]);
  const [liveMetrics, setLiveMetrics] = useState<LiveMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sortBy, setSortBy] = useState<'requests' | 'response_time' | 'error_rate'>('requests');
  const [apiAvailable, setApiAvailable] = useState(true);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  const fetchSystemHealth = useCallback(async () => {
    try {
      const response = await performanceAPI.getSystemHealth();
      setSystemHealth(response.data);
      setApiAvailable(true);
    } catch (error: any) {
      logger.error('System health fetch error:', error);
      if (error.response?.status === 404) {
        setApiAvailable(false);
      } else {
        showError(error.message || 'Failed to load system health');
      }
    }
  }, [showError]);

  const fetchEndpointStats = useCallback(async () => {
    try {
      const response = await performanceAPI.getEndpointStats(sortBy, 15);
      // apiServices already returns response.data, so just access endpoints directly
      setEndpointStats(response.endpoints || []);
    } catch (error: any) {
      logger.error('Endpoint stats fetch error:', error);
      if (error.response?.status === 404) {
        logger.warn('Performance endpoints API not available');
        return;
      }
      showError(error.message || 'Failed to load endpoint statistics');
    }
  }, [sortBy, showError]);

  const fetchLiveMetrics = useCallback(async () => {
    try {
      const response = await performanceAPI.getLiveMetrics();
      setLiveMetrics(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        logger.warn('Live metrics API not available');
        return;
      }
      logger.warn('Failed to load live metrics:', error);
    }
  }, []);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSystemHealth(),
        fetchEndpointStats(),
        fetchLiveMetrics()
      ]);
    } finally {
      setLoading(false);
    }
  }, [fetchSystemHealth, fetchEndpointStats, fetchLiveMetrics]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
  };

  // Reset metrics feature disabled for production safety
  // Metrics are in-memory and reset automatically on server restart
  // const handleResetMetrics = async () => {
  //   try {
  //     await performanceAPI.resetMetrics();
  //     showSuccess('Performance metrics reset successfully');
  //     await loadAllData();
  //   } catch (error: any) {
  //     showError(error.message || 'Failed to reset metrics');
  //   }
  // };



  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon color="success" />;
      case 'degraded': return <WarningIcon color="warning" />;
      case 'slow': return <WarningIcon color="warning" />;
      case 'error': return <ErrorIcon color="error" />;
      default: return <SpeedIcon />;
    }
  };

  const formatBytes = (bytes: number | undefined) => {
    if (bytes === undefined || bytes === null || Number.isNaN(bytes)) {
      return '0.0 MB';
    }
    return `${bytes.toFixed(1)} MB`;
  };

  const formatDuration = (seconds: number | undefined) => {
    if (seconds === undefined || seconds === null || Number.isNaN(seconds)) {
      return '0h 0m';
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Initial data load
  useEffect(() => {
    if (!isAdmin) return;
    loadAllData();
  }, [isAdmin, loadAllData]);

  if (!isAdmin) {
    return (
      <Box p={3}>
        <ResponsivePageHeader
          title="Praćenje Performansi"
          subtitle="Monitoring performansi sistema i zdravlje aplikacije"
          userInfo={{
            name: user?.email?.split('@')[0] || 'Korisnik',
            email: user?.email,
            company: user?.company_name || 'OmniPass Portal',
            role: user?.role
          }}
        />
        <Alert severity="error" sx={{ mt: 2 }}>
          Nemate dozvolu za pristup performance monitoring dashboard-u. Potrebne su admin privilegije.
        </Alert>
      </Box>
    );
  }

  // Show message if performance monitoring is not available
  if (!apiAvailable && !loading) {
    return (
      <Box p={3}>
        <ResponsivePageHeader
          title="Praćenje Performansi"
          subtitle="Praćenje performansi sistema u realnom vremenu"
          icon={<SpeedIcon />}
          userInfo={{
            name: user?.email?.split('@')[0] || 'Korisnik',
            email: user?.email,
            company: user?.company_name || 'OmniPass Portal',
            role: user?.role
          }}
          actions={
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              Osveži
            </Button>
          }
        />
        <Alert severity="warning" sx={{ mt: 2 }}>
          API za praćenje performansi nije dostupan. Molimo proverite da li je backend server pokrenut i da li su performance endpoint-i konfigurisani.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <ResponsivePageHeader
        title="Praćenje Performansi"
        subtitle="Praćenje performansi sistema u realnom vremenu"
        icon={<SpeedIcon />}
        userInfo={{
          name: user?.email?.split('@')[0] || 'Korisnik',
          email: user?.email,
          company: user?.company_name || 'OmniPass Portal',
          role: user?.role
        }}
        actions={
          <Box display="flex" gap={1}>
            <Tooltip title="Osveži metrike">
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleRefresh}
                disabled={refreshing}
                startIcon={<RefreshIcon />}
              >
                {refreshing ? 'Osvežavanje...' : 'Osveži'}
              </Button>
            </Tooltip>
          </Box>
        }
      />

      {/* System Health Overview */}
      {systemHealth && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  {getStatusIcon(systemHealth.status)}
                  <Typography variant="h6" ml={1}>
                    Status sistema
                  </Typography>
                </Box>
                <Chip
                  label={systemHealth.status.toUpperCase()}
                  color={systemHealth.status === 'healthy' ? 'success' : 'warning'}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Vreme rada: {formatDuration(systemHealth.uptime_seconds)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <TimelineIcon color="primary" />
                  <Typography variant="h6" ml={1}>
                    Vreme Odgovora
                  </Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {systemHealth.avg_response_time.toFixed(1)}ms
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Prosečno vreme odgovora
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <TrendingUpIcon color="success" />
                  <Typography variant="h6" ml={1}>
                    Zahtevi
                  </Typography>
                </Box>
                <Typography variant="h4" color="success.main">
                  {systemHealth.total_requests.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ukupno procesuiranih zahteva
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <ErrorIcon color={systemHealth.error_rate > 5 ? 'error' : 'success'} />
                  <Typography variant="h6" ml={1}>
                    Stopa Grešaka
                  </Typography>
                </Box>
                <Typography variant="h4" color={systemHealth.error_rate > 5 ? 'error.main' : 'success.main'}>
                  {systemHealth.error_rate.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {systemHealth.total_errors} ukupno grešaka
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Database and Memory Stats */}
      {systemHealth && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <StorageIcon color="primary" />
                  <Typography variant="h6" ml={1}>
                    Performanse Baze Podataka
                  </Typography>
                  <Chip
                    label={systemHealth.database?.status || 'unknown'}
                    color={systemHealth.database?.status === 'healthy' ? 'success' : 'warning'}
                    size="small"
                    sx={{ ml: 'auto' }}
                  />
                </Box>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    Vreme Odgovora: {systemHealth.database?.response_time_ms || 0}ms
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(((systemHealth.database?.response_time_ms || 0) / 100) * 100, 100)}
                    color={(systemHealth.database?.response_time_ms || 0) > 50 ? 'warning' : 'success'}
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Veličina Pool-a: {systemHealth.database?.pool_size || 0} |
                  Aktivne: {systemHealth.database?.connections_checked_out || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <MemoryIcon color="primary" />
                  <Typography variant="h6" ml={1}>
                    Korišćenje Memorije
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    RSS: {formatBytes(systemHealth.memory_usage?.rss_mb)}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(((systemHealth.memory_usage?.rss_mb || 0) / 512) * 100, 100)}
                    color={(systemHealth.memory_usage?.rss_mb || 0) > 256 ? 'warning' : 'success'}
                    sx={{ mt: 1, mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    VMS: {formatBytes(systemHealth.memory_usage?.vms_mb)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Aktivne Konekcije: {systemHealth.active_connections}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Live Activity */}
      {liveMetrics && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" mb={2}>
              Aktivnost u Realnom Vremenu (Poslednjih 5 minuta)
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Ažurirano: {format(new Date(liveMetrics.timestamp), 'HH:mm:ss')}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Endpoint</TableCell>
                    <TableCell>Metoda</TableCell>
                    <TableCell align="right">Skorašnji Zahtevi</TableCell>
                    <TableCell align="right">Prosečan Odgovor</TableCell>
                    <TableCell>Poslednji Zahtev</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {liveMetrics.recent_activity.map((activity) => (
                    <TableRow key={`${activity.endpoint}-${activity.method}`}>
                      <TableCell>{activity.endpoint}</TableCell>
                      <TableCell>
                        <Chip
                          label={activity.method}
                          size="small"
                          color={activity.method === 'GET' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell align="right">{activity.recent_requests}</TableCell>
                      <TableCell align="right">{activity.avg_response_time}ms</TableCell>
                      <TableCell>
                        {format(new Date(activity.last_request), 'HH:mm:ss')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Endpoint Performance */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Performanse Endpoint-a
            </Typography>
            <Box display="flex" gap={1}>
              {(['requests', 'response_time', 'error_rate'] as const).map((sort) => (
                <Button
                  key={sort}
                  size="small"
                  variant={sortBy === sort ? 'contained' : 'outlined'}
                  onClick={() => setSortBy(sort)}
                >
                  {(() => {
                    if (sort === 'requests') return 'Najkorišćeniji';
                    if (sort === 'response_time') return 'Najsporiji';
                    return 'Najviše Grešaka';
                  })()}
                </Button>
              ))}
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Endpoint</TableCell>
                  <TableCell>Metoda</TableCell>
                  <TableCell align="right">Zahtevi</TableCell>
                  <TableCell align="right">Prosečan Odgovor</TableCell>
                  <TableCell align="right">P95</TableCell>
                  <TableCell align="right">Stopa Grešaka</TableCell>
                  <TableCell align="right">Zah/min</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {endpointStats.map((stat) => (
                  <TableRow key={`${stat.endpoint}-${stat.method}`}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {stat.endpoint}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={stat.method}
                        size="small"
                        color={(() => {
                          if (stat.method === 'GET') return 'primary';
                          if (stat.method === 'POST') return 'success';
                          if (stat.method === 'PUT') return 'warning';
                          return 'error';
                        })()}
                      />
                    </TableCell>
                    <TableCell align="right">{stat.total_requests.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Typography
                        color={(() => {
                          if (stat.avg_response_time > 1000) return 'error';
                          if (stat.avg_response_time > 500) return 'warning';
                          return 'success';
                        })()}
                      >
                        {stat.avg_response_time.toFixed(1)}ms
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{stat.p95_response_time.toFixed(1)}ms</TableCell>
                    <TableCell align="right">
                      <Typography
                        color={(() => {
                          if (stat.error_rate > 5) return 'error';
                          if (stat.error_rate > 1) return 'warning';
                          return 'success';
                        })()}
                      >
                        {stat.error_rate.toFixed(1)}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{stat.requests_per_minute.toFixed(1)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PerformanceMonitoringDashboard;
